<template>
  <ValidationProvider
    v-slot="{ errors, required }"
    :ref="refName"
    :vid="name"
    :name="name"
    :rules="rules"
    tag="div">
    <div
      v-click-outside="reset"
      :class="[
        'dropdown w-full flex',
        {
          open: open,
          'dropdown-error': !!errors && Array.isArray(errors) && !!errors[0]
        }
      ]"
      @click="toggleDropdown()"
      @keydown.esc="reset()">
      <div class="dropdown__slot-prepend">
        <slot
          name="prepend"
          v-bind="{ name, errors }">
          <label
            v-if="label"
            :for="name">
            {{ label }}
          </label>
        </slot>
      </div>
      <div class="dropdown__slot-default selection flex w-full relative">
        <span
          :class="[
            'selection-value',
            {
              open: open
            }]">
          <slot
            name="value"
            v-bind="{ value: innerValue }">
            {{ innerItemValue }}
          </slot>
        </span>
        <input
          v-model="innerItemValue"
          v-bind="$attrs"
          :name="name"
          type="hidden">
        <input
          ref="search"
          v-model="search"
          class="rounded-[8px] pl-2"
          :class="[
            'selection-search',
            {
              open: open
            }]"
          :placeholder="searchPlaceholder"
          :required="required"
          :disabled="disabled"
          type="text"
          @keydown="keyboardNav($event)"
          @blur="onBlur()">
        <span class="arrow-icon">
          <slot
            name="arrow-icon"
            v-bind="{ open, disabled }">
            <span
              v-if="!disabled"
              class="dropdown-arrow pr-5">
              {{ open ? "▲" : "▼" }}
            </span>
          </slot>
        </span>
      </div>
      <div class="dropdown__slot-append">
        <slot
          name="append"
          v-bind="{ name, errors }">
        </slot>
      </div>
      <ul
        v-if="open"
        ref="list"
        class="dropdown-list rounded-b-[8px]">
        <li
          v-for="(item, index) in innerItems"
          :key="index"
          :class="['dropdown-item', {
            highlighted: selectedIndex === index
          }]"
          @click="select(item)"
          @mousemove="selectedIndex = index">
          <slot
            name="item"
            v-bind="{ name, errors, item }">
            <strong>{{ itemText ? item[itemText] : item }}</strong>
          </slot>
        </li>
      </ul>
    </div>
    <div
      v-if="!hideDetail"
      :class="['text-messages', { 'text-messages-error': !!errors && Array.isArray(errors) && !!errors[0] }]">
      <span>{{ errors[0] }}</span>
    </div>
  </ValidationProvider>
</template>

<script>
export default {
  name: 'DropDown',
  props: {
    value: {
      type: [String, Object],
      default: null
    },
    rules: {
      type: [String, Object],
      default: null
    },
    name: {
      type: String,
      required: true,
      default: null
    },
    label: {
      type: String,
      default: null
    },
    inputClass: {
      type: String,
      default: ''
    },
    disabled: {
      type: Boolean,
      default: false
    },
    items: {
      type: Array,
      default: () => []
    },
    itemSearch: {
      type: String,
      default: null
    },
    itemValue: {
      type: String,
      default: null
    },
    itemText: {
      type: String,
      default: null
    },
    returnObject: {
      type: Boolean
    },
    hideDetail: {
      type: Boolean
    },
    placeholder: {
      type: String,
      default: null
    }
  },
  data () {
    return {
      search: null,
      open: false,
      selectedIndex: -1
    }
  },
  computed: {
    innerValue: {
      get () {
        return this.value
      },
      set (val) {
        this.$emit('input', val)
      }
    },
    searchInput () {
      return this.itemSearch ? this.innerValue[this.itemSearch] : this.innerValue
    },
    innerItemValue () {
      return this.itemText && this.innerValue ? this.innerValue[this.itemText] : this.innerValue
    },
    innerItems () {
      const search = this.search?.toLowerCase() || null
      if (search) {
        return this.itemText
          ? this.items.filter((item) => item[this.itemText].toLowerCase().includes(search))
          : this.items.filter((item) => item.toLowerCase().includes(search))
      }
      return this.items
    },
    searchPlaceholder () {
      if (this.open) {
        return this.innerItemValue
      }
      return this.innerItemValue ? null : this.placeholder
    },
    refName () {
      return `validation-provider-${this.name}`
    }
  },
  methods: {
    keyboardNav (e) {
      if (e.keyCode === 40) {
        // down arrow
        e.preventDefault()
        this.open = true
        if (this.selectedIndex === null) {
          this.selectedIndex = 0
        } else {
          this.selectedIndex = Math.min(this.innerItems.length - 1, this.selectedIndex + 1)
        }
        const selEle = this.$refs.list?.children[this.selectedIndex] || null
        if (selEle && selEle.offsetTop + selEle.clientHeight
          > this.$refs.list.scrollTop + this.$refs.list.clientHeight) {
          this.$refs.list.scrollTop = selEle.offsetTop
            - this.$refs.list.clientHeight
            + selEle.clientHeight
        }
      } else if (e.keyCode === 38) {
        // up arrow
        e.preventDefault()
        this.open = true
        if (this.selectedIndex === null) {
          this.selectedIndex = this.innerItems.length - 1
        } else {
          this.selectedIndex = Math.max(0, this.selectedIndex - 1)
        }
        const selEle = this.$refs.list?.children[this.selectedIndex] || null
        if (selEle && selEle.offsetTop < this.$refs.list.scrollTop) {
          this.$refs.list.scrollTop = selEle.offsetTop
        }
      } else if (e.keyCode === 13) {
        // enter key
        if (this.selectedIndex !== null) {
          this.select(this.innerItems[this.selectedIndex])
        }
        this.reset()
      } else {
        this.selectedIndex = 0
        const selEle = this.$refs.list?.children[this.selectedIndex] || null
        if (selEle && selEle.offsetTop + selEle.clientHeight
          > this.$refs.list.scrollTop + this.$refs.list.clientHeight) {
          this.$refs.list.scrollTop = selEle.offsetTop
            - this.$refs.list.clientHeight
            + selEle.clientHeight
        }
      }
    },
    toggleDropdown () {
      if (!this.disabled) {
        if (this.open) {
          this.reset()
        } else {
          this.open = true
          this.$refs.search.focus()
        }
      }
    },
    reset () {
      this.open = false
      this.search = null
      this.$refs.search.blur()
    },
    select (item) {
      this.innerValue = item
      this.$emit('select', item)
    },
    onBlur () {
      this.$refs[this.refName].validate()
    }
  }
}
</script>

<style lang="postcss" scoped>
/* input {
  border-bottom: 1px solid;
  border-radius: 0;
  width: 100%;
  padding: 3px 5px;
  color: #000;
  transition: border-color 0.25s;
} */
.dropdown.dropdown-error {
  border-color: var(--color-danger);
}
.dropdown {
  display: flex;
  position: relative;
  width: 100%;
  height: 48px;
}
.selection {
  display: flex;
  justify-content: center;
}
.selection-value {
  position: absolute;
  left: 0;
  top: 50%;
  transform: translateY(-50%);
  width: 100%;
}
.selection-value.open {
  z-index: -1;
}
.arrow-icon {
  position: absolute;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
}
.selection-search {
  width: 100%;
}
.dropdown-list {
  z-index: 1;
  padding: 0;
  margin: 0;
  text-align: left;
  list-style: none;
  height: 0;
  max-height: 200px;
  overflow-y: scroll;
  position: absolute;
  left: 0;
  top: 50px;
  background-color: #fff;
  border: 0px solid #ccc;
  width: 100%;
  height: 48px;
  transition: all .25s;
}
.open .dropdown-list {
  height: fit-content;
  max-height: 200px;
  border-width: 1px;
}
.dropdown-item {
  padding: 4px 15px;
  cursor: pointer;
}
.highlighted {
  background-color: #d8d8d8;
}
.text-messages {
  font-size: 12px;
  height: 18px;
}
.text-messages-error {
  color: var(--color-danger);
}
.text-field-error input {
  border-color: var(--color-danger);
}
</style>
